import React, { useEffect, useRef } from 'react'
import { useLazyQuery } from '@apollo/client'
import { useSlerp } from '@slerp/client'
import { useMerchant } from 'hooks/useGlobalContext'
import { useLoyaltyOverview } from 'hooks/useGlobalContext/useLoyaltyOverview'
import {
  QUERY_LOYALTY_CUSTOMERS_AGGREGATES,
  QUERY_LOYALTY_CUSTOMERS_AGGREGATES_OPT_IN,
  QUERY_BULK_LOYALTY_CUSTOMERS_AGGREGATES,
  QUERY_BULK_LOYALTY_CUSTOMERS_AGGREGATES_OPT_IN,
  QUERY_LOYALTY_CUSTOMERS_AND_BULK_LOYALTY_AGGREGATES,
  QUERY_LOYALTY_CUSTOMERS_AND_BULK_LOYALTY_CUSTOMERS_AGGREGATES_OPT_IN,
  QUERY_GET_LOYALTY_CUSTOMERS_LIST,
  QUERY_GET_LOYALTY_CUSTOMERS_EXPORT_LIST,
  QUERY_GET_LOYALTY_CUSTOMERS_LIST_COUNT
} from 'components/Loyalty/LoyaltyQueries'
import { StoreType } from 'components/Loyalty/types'
import { toLoyaltySourceParam } from 'components/Loyalty/utils'
import { LoaderProps } from './types'

const queryDateTimeFormat = 'YYYY-MM-DDTHH:mm:ss'

// Runs every Loyalty Overview query and writes results/trigger-functions
// into useLoyaltyOverview's Context. Mounted in LoyaltyWrapper.tsx above
// the Loyalty tabs so this state survives switching tabs and back.
// `isLoaded` is a pure passthrough of `isParentLoaded`: this Loader's own
// fetch is deliberately deferred until the Overview tab is actually
// visited (LoyaltyOverview.tsx's mount effect decides when to call
// `loadCustomersAggregate`), so it must never gate the rest of the Loyalty
// section on its own data being ready.
export const LoyaltyOverviewLoader = ({
  isParentLoaded = true,
  children
}: LoaderProps) => {
  const { merchantLocations } = useMerchant()
  const { user } = useSlerp()
  const {
    startDate,
    endDate,
    storeIds,
    setStoreIds,
    optInFilter,
    loyaltySource,
    customers,
    setCustomers,
    setCustomersTotalCount,
    setCustomersTotalCountLoading,
    setLoyaltyCustomersList,
    setPaginatedCustomersList,
    customerAggregateQuery,
    setCustomerAggregateQuery,
    selectedDropdownOption,
    setHasLoadedOverviewData,
    setLoadCustomersAggregate,
    setLoyaltyCustomersAggregateRefetch,
    setLoyaltyCustomersAggregateLoading,
    setRefetchLoyaltyCustomersList,
    setLoyaltyCustomersListLoading,
    setLoadLoyaltyCustomersPage,
    setLoyaltyCustomersPageLoading,
    setLoadLoyaltyCustomersExportPage
  } = useLoyaltyOverview()

  useEffect(() => {
    if (merchantLocations.length > 0) {
      setStoreIds(merchantLocations.map(({ id }: StoreType) => id))
    }
  }, [merchantLocations])

  useEffect(() => {
    let loyaltyAggregateQuery = null

    switch (selectedDropdownOption) {
      case 'all_stores':
        loyaltyAggregateQuery = optInFilter
          ? QUERY_LOYALTY_CUSTOMERS_AND_BULK_LOYALTY_CUSTOMERS_AGGREGATES_OPT_IN
          : QUERY_LOYALTY_CUSTOMERS_AND_BULK_LOYALTY_AGGREGATES

        break

      case 'bulk_operation':
        loyaltyAggregateQuery = optInFilter
          ? QUERY_BULK_LOYALTY_CUSTOMERS_AGGREGATES_OPT_IN
          : QUERY_BULK_LOYALTY_CUSTOMERS_AGGREGATES
        break

      default:
        loyaltyAggregateQuery = optInFilter
          ? QUERY_LOYALTY_CUSTOMERS_AGGREGATES_OPT_IN
          : QUERY_LOYALTY_CUSTOMERS_AGGREGATES
        break
    }

    if (loyaltyAggregateQuery) {
      setCustomerAggregateQuery(loyaltyAggregateQuery)
    }
  }, [selectedDropdownOption, optInFilter])

  const [
    loadCustomersAggregate,
    {
      data: loyaltyCustomersAggregateData,
      refetch: loyaltyCustomersAggregateRefetch,
      loading: loyaltyCustomersAggregateLoading
    }
  ] = useLazyQuery(customerAggregateQuery, {
    variables: {
      merchantId: user?.merchant?.id ?? '',
      startDate: startDate.startOf('day').format(queryDateTimeFormat),
      endDate: endDate.endOf('day').format(queryDateTimeFormat),
      storeIds: storeIds
    },
    onCompleted: () => {
      setHasLoadedOverviewData(true)
      loadLoyaltyCustomersList()
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    setLoadCustomersAggregate(() => loadCustomersAggregate)
  }, [loadCustomersAggregate])

  useEffect(() => {
    setLoyaltyCustomersAggregateRefetch(() => loyaltyCustomersAggregateRefetch)
  }, [loyaltyCustomersAggregateRefetch])

  useEffect(() => {
    setLoyaltyCustomersAggregateLoading(loyaltyCustomersAggregateLoading)
  }, [loyaltyCustomersAggregateLoading, setLoyaltyCustomersAggregateLoading])

  const [
    loadLoyaltyCustomersListCount,
    { loading: customersTotalCountLoading }
  ] = useLazyQuery(QUERY_GET_LOYALTY_CUSTOMERS_LIST_COUNT, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    setCustomersTotalCountLoading(customersTotalCountLoading)
  }, [customersTotalCountLoading, setCustomersTotalCountLoading])

  // Discards a stale response the same way loadLoyaltyCustomersPage does
  // below — rapid filter changes can otherwise let an older count overwrite
  // a newer one that already resolved.
  const latestCountRequestRef = useRef(0)

  const refreshTotalCount = (customerIds: string[]) => {
    const requestId = ++latestCountRequestRef.current
    loadLoyaltyCustomersListCount({
      variables: {
        merchantId: user?.merchant?.id ?? '',
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        storeIds,
        customerIds,
        optInFilter,
        loyaltySource: toLoyaltySourceParam(loyaltySource)
      }
    }).then((result) => {
      if (latestCountRequestRef.current === requestId) {
        setCustomersTotalCount(result.data?.loyaltyCustomersListCount ?? 0)
      }
    })
  }

  // Source for the raw customer set (avg-basket stats, customerIds);
  // refreshTotalCount below handles the total separately.
  useEffect(() => {
    if (
      loyaltyCustomersAggregateData &&
      loyaltyCustomersAggregateData.customers
    ) {
      setCustomers(loyaltyCustomersAggregateData.customers)
      refreshTotalCount(
        loyaltyCustomersAggregateData.customers.map(
          ({ id }: { id: string }) => id
        )
      )
    }
  }, [loyaltyCustomersAggregateData])

  // getLoyaltyCustomersListCount is the source of truth for the total —
  // loyaltySource is its own filter, independent of the aggregate query
  // above, so a change here refreshes the count without refetching that.
  useEffect(() => {
    if (customers.length === 0) return
    refreshTotalCount(customers.map(({ id }) => id))
  }, [loyaltySource])

  const [
    loadLoyaltyCustomersList,
    {
      data: loyaltyCustomersListData,
      refetch: refetchLoyaltyCustomersList,
      loading: loyaltyCustomersListLoading
    }
  ] = useLazyQuery(QUERY_GET_LOYALTY_CUSTOMERS_LIST, {
    variables: {
      merchantId: user?.merchant?.id ?? '',
      startDate: startDate.format('YYYY-MM-DD'),
      endDate: endDate.format('YYYY-MM-DD'),
      storeIds: storeIds,
      customerIds: (customers || []).map(({ id }) => id),
      optInFilter,
      limit: 100,
      offset: 0,
      sortBy: 'orders',
      sortDirection: 'desc'
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    setRefetchLoyaltyCustomersList(() => refetchLoyaltyCustomersList)
  }, [refetchLoyaltyCustomersList])

  useEffect(() => {
    setLoyaltyCustomersListLoading(loyaltyCustomersListLoading)
  }, [loyaltyCustomersListLoading, setLoyaltyCustomersListLoading])

  useEffect(() => {
    setLoyaltyCustomersList(
      loyaltyCustomersListData?.loyaltyCustomersList ?? []
    )
  }, [loyaltyCustomersListData])

  // Dedicated to whichever page 2+ the merchant has navigated to — kept
  // separate from the list query above so paginating the table never
  // overwrites the stats/CSV/count source.
  const [loadLoyaltyCustomersPage, { loading: loyaltyCustomersPageLoading }] =
    useLazyQuery(QUERY_GET_LOYALTY_CUSTOMERS_LIST, {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    })

  // Tracks the most recently requested page so an out-of-order response
  // (e.g. clicking page 2 then page 3 before page 2's response lands) can't
  // overwrite paginatedCustomersList after a newer page already has.
  const latestPageRequestRef = useRef(0)

  useEffect(() => {
    setLoyaltyCustomersPageLoading(loyaltyCustomersPageLoading)
  }, [loyaltyCustomersPageLoading, setLoyaltyCustomersPageLoading])

  useEffect(() => {
    setLoadLoyaltyCustomersPage(
      () => (options: { variables: Record<string, unknown> }) => {
        const requestId = ++latestPageRequestRef.current
        return loadLoyaltyCustomersPage(options).then((result) => {
          if (latestPageRequestRef.current === requestId) {
            setPaginatedCustomersList(result.data?.loyaltyCustomersList ?? [])
          }
          return result
        })
      }
    )
  }, [loadLoyaltyCustomersPage])

  // Triggered only from LoyaltyOverview.tsx's Export button (handleExport),
  // once per page it needs — never fired automatically like the queries above.
  const [loadLoyaltyCustomersExportPage] = useLazyQuery(
    QUERY_GET_LOYALTY_CUSTOMERS_EXPORT_LIST,
    { fetchPolicy: 'no-cache', notifyOnNetworkStatusChange: true }
  )

  useEffect(() => {
    setLoadLoyaltyCustomersExportPage(() => loadLoyaltyCustomersExportPage)
  }, [loadLoyaltyCustomersExportPage])

  const isLoaded = isParentLoaded

  return <React.Fragment>{children(isLoaded)}</React.Fragment>
}
